import React, { useEffect, useMemo, useState } from "react";
import {
    Col,
    Card, Row,
    CardBody,
    Input, Label,
    Modal, ModalBody, ModalHeader,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { SearchTableDMS } from "../../pages/Tables/ReactTables/ReactTable";
import { CompactStatsRow } from "../../Components/Common/DashboardKit";

import api from "../../api";
// Round DMS-P2-1 (2026) — audit Q1: the "Create new" button follows the
// dms.create module (Access Management), same resolver RoleProtected uses.
import useAuth from "../Personal Attendance/useAuth";
import "react-toastify/dist/ReactToastify.css";

/**
 * DMS — All Documents list with summary widgets and filter strip.
 *
 * Pass 2 UI update (2026):
 *  - The six summary widgets are now rendered via the shared
 *    CompactStatsRow + StatTile components instead of an inline
 *    card-animate / CountUp / avatar-title block. Same six metrics,
 *    same labels, same field mapping off the `summary` payload.
 *  - Unused imports (Container, Pagination/PaginationItem/PaginationLink,
 *    CountUp, classnames, useLocation, react-testing-library render,
 *    ToastContainer, toast) were removed — they were never referenced
 *    in the markup. ToastContainer was unused at this level; the
 *    parent DocumentManagementSystem/Index.js already provides one
 *    via react-toastify.
 *
 * EVERYTHING ELSE PRESERVED EXACTLY:
 *  - useState hooks (documents, summary, loading, docType, show,
 *    search, totalCount, showCreateTypeModal) — same names, same
 *    initial values.
 *  - userName from sessionStorage authUser.uid — unchanged.
 *  - The 300ms debounced useEffect on [docType, show, search] —
 *    unchanged.
 *  - fetchDocuments — same URL ("/dms/alldocuments"), same query
 *    params (docType, show, search, author).
 *  - The commented-out renderPagination block — KEPT as a comment;
 *    do not delete, it documents pending pagination work.
 *  - The radio-button filter strips for docType and show — same
 *    options, same setter calls.
 *  - documentTypes array, handleCreateType handler, the create-type
 *    modal — all unchanged.
 *  - SearchTableDMS — same props (data, show, loading,
 *    onRowDoubleClick).
 *  - Both console.log calls are kept (they were already present).
 */

const AllDms = () => {
    const userName = JSON.parse(sessionStorage.getItem("authUser"))?.uid;
    const navigate = useNavigate();
    // Round DMS-P2-1 — audit Q1: creation follows Access Management.
    const { canAccessModule } = useAuth();
    // EPO-RESTORE fallback: shared EffectiveAccessService is pristine
    // (no dms.create key yet) — fall back to dms.all so the button is not
    // hidden from DMS users. Remove "|| canAccessModule(\"dms.all\")" once
    // the dms.create entry is re-applied to EffectiveAccessService.
    const canCreate = canAccessModule("dms.create") || canAccessModule("dms.all");
    const [documents, setDocuments] = useState([]);
    const [summary, setSummary] = useState(null);

    const [loading, setLoading] = useState(false);

    const [docType, setDocType] = useState("ALL");
    const [show, setShow] = useState("Released");
    const [search, setSearch] = useState("");


    const [totalCount, setTotalCount] = useState(0);
    const [showCreateTypeModal, setShowCreateTypeModal] = useState(false);

    console.log("Author Code: ", userName);

    /**
     * Map the DMS summary payload to StatTile-shaped objects.
     * Field names (total, released, archived, myDocs, awaitingMyApproval,
     * awaitingRelease) match the backend response and MUST NOT be
     * renamed.
     *
     * Hotfix 10 — accepts `setShow` so each tile carries an onClick
     * that wires the click to the matching radio-chip filter value.
     * Lets users click a tile to filter the list instead of using the
     * radio buttons. Filter keys must match the radio array values:
     * "All" / "Released" / "Archived" / "MyDocs" / "AwaitingMyApproval".
     */
    const buildTaskWidgets = (summary, setShow) => {
        if (!summary) {
            return [
                { key: "all", label: "All", value: 0, icon: "ri-ticket-2-line", tone: "info", loading: true,
                  onClick: () => setShow("All") },
                { key: "released", label: "Released", value: 0, icon: "mdi mdi-timer-sand", tone: "warning", loading: true,
                  onClick: () => setShow("Released") },
                { key: "archived", label: "Archived", value: 0, icon: "ri-checkbox-circle-line", tone: "success", loading: true,
                  onClick: () => setShow("Archived") },
                { key: "myDocs", label: "My Doc", value: 0, icon: "ri-delete-bin-line", tone: "danger", loading: true,
                  onClick: () => setShow("MyDocs") },
                { key: "voids", label: "Void", value: 0, icon: "ri-forbid-2-line", tone: "secondary", loading: true,
                  onClick: () => setShow("Void") },
                { key: "awaitingMyApproval", label: "Awaiting My Approval", value: 0, icon: "ri-time-line", tone: "primary", loading: true,
                  onClick: () => setShow("AwaitingMyApproval") },
                // Round R14 (2026) — "Awaiting Release" tile removed from
                // /document-management per user feedback. Reason: the
                // tile/chip here weren't role-gated (any user could click,
                // including those without dms.release permission), whereas
                // the dedicated /dms/awaiting-release route is properly
                // RoleProtected via the dms.release module gate (R1 + R2).
                // Using one access path (the dedicated tab) keeps the
                // permission boundary clear.
            ];
        }
        return [
            {
                key: "all",
                label: "All",
                value: summary.total || 0,
                icon: "ri-ticket-2-line",
                tone: "info",
                onClick: () => setShow("All"),
            },
            {
                key: "released",
                label: "Released",
                value: summary.released || 0,
                icon: "mdi mdi-timer-sand",
                tone: "warning",
                onClick: () => setShow("Released"),
            },
            {
                key: "archived",
                label: "Archived",
                value: summary.archived || 0,
                icon: "ri-checkbox-circle-line",
                tone: "success",
                onClick: () => setShow("Archived"),
            },
            {
                key: "voids",
                label: "Void",
                value: summary.voids || 0,
                icon: "ri-forbid-2-line",
                tone: "secondary",
                onClick: () => setShow("Void"),
            },
            {
                key: "myDocs",
                label: "My Doc",
                value: summary.myDocs || 0,
                icon: "ri-delete-bin-line",
                tone: "danger",
                onClick: () => setShow("MyDocs"),
            },
            {
                key: "awaitingMyApproval",
                label: "Awaiting My Approval",
                value: summary.awaitingMyApproval || 0,
                icon: "ri-time-line",
                tone: "primary",
                onClick: () => setShow("AwaitingMyApproval"),
            },
            // Round R14 (2026) — awaitingRelease tile removed. See loading
            // branch comment above for rationale; the dedicated
            // /dms/awaiting-release route is the single proper access path.
        ];
    };



    // const renderPagination = () => {
    //     if (totalPages <= 1) return null;

    //     const startPage = Math.max(1, page - 2);
    //     const endPage = Math.min(totalPages, page + 2);

    //     const pages = [];
    //     for (let i = startPage; i <= endPage; i++) {
    //         pages.push(i);
    //     }

    //     return (
    //         <div className="d-flex justify-content-between align-items-center mt-3">
    //             <div>
    //                 Showing {(page - 1) * pageSize + 1}
    //                 {" - "}
    //                 {Math.min(page * pageSize, totalCount)}
    //                 {" of "}
    //                 {totalCount}
    //             </div>

    //             <Pagination>

    //                 <PaginationItem disabled={page === 1}>
    //                     <PaginationLink first onClick={() => setPage(1)} />
    //                 </PaginationItem>

    //                 <PaginationItem disabled={page === 1}>
    //                     <PaginationLink previous onClick={() => setPage(page - 1)} />
    //                 </PaginationItem>

    //                 {pages.map(p => (
    //                     <PaginationItem key={p} active={p === page}>
    //                         <PaginationLink onClick={() => setPage(p)}>
    //                             {p}
    //                         </PaginationLink>
    //                     </PaginationItem>
    //                 ))}

    //                 <PaginationItem disabled={page === totalPages}>
    //                     <PaginationLink next onClick={() => setPage(page + 1)} />
    //                 </PaginationItem>

    //                 <PaginationItem disabled={page === totalPages}>
    //                     <PaginationLink last onClick={() => setPage(totalPages)} />
    //                 </PaginationItem>

    //             </Pagination>
    //         </div>
    //     );
    // };


    const fetchDocuments = async () => {
        try {
            setLoading(true);

            const res = await api.get("/dms/alldocuments", {
                params: {
                    docType,
                    show,
                    search,
                    author: userName ? userName : null,
                    // Round DMS-P2-4 (G7): backend default is now bounded;
                    // this page's client-side table asks for its (capped)
                    // window explicitly. Server paging remains available.
                    pageSize: 2000
                }
            });

            setDocuments(res.data.results || []);
            setSummary(res.data.summary);
            setTotalCount(res.data.totalCount || 0);

            console.log("Summaryyyyyy :", documents);

        } catch (err) {
            console.error("DMS fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchDocuments();
        }, 300);

        return () => clearTimeout(timer);
    }, [docType, show, search]);

    // Hotfix 10 — pass setShow so each tile's onClick can set the
    // filter radio. setShow is a stable React setState reference and
    // does not need to be listed in the useMemo deps.
    const widgetData = useMemo(() => buildTaskWidgets(summary, setShow), [summary]);
    // Reset to page 1 when filter changes

    const documentTypes = [
        {
            type: "ECN",
            icon: "ri-settings-3-line",
            color: "primary",
            description: "Engineering Change Notice"
        },
        {
            type: "SOP",
            icon: "ri-book-open-line",
            color: "success",
            description: "Standard Operating Procedure"
        },
        {
            type: "WI",
            icon: "ri-file-list-3-line",
            color: "warning",
            description: "Work Instruction"
        },
        {
            type: "FCD",
            icon: "ri-folder-chart-line",
            color: "info",
            description: "Form Control Document"
        },
        {
            type: "LIT",
            icon: "ri-file-text-line",
            color: "secondary",
            description: "Literature Document"
        }
    ];
    const handleCreateType = (type) => {
        setShowCreateTypeModal(false);
        navigate(`/dms/create/${type.toLowerCase()}`);
    };

    return (
        <React.Fragment>
            <div className="row">
                <Col lg={12}>
                    {/* Stat tiles — six metrics, now rendered via shared kit */}
                    <CompactStatsRow
                        stats={widgetData}
                        loading={loading && !summary}
                        columns={{ xl: 4, lg: 6, sm: 6 }}
                        className="mb-3"
                    />
                    <Card>
                        <CardBody>

                            <div className="card" id="tasksList">
                                <div className="card-header border-0">
                                    <div className="d-flex align-items-center">
                                        <h5 className="card-title mb-0 flex-grow-1">Documents ({totalCount})</h5>
                                        <div className="flex-shrink-0">
                                            {/* Round DMS-P2-1 — audit Q1: button visible only with the
                                                dms.create module; backend enforces the same gate. */}
                                            {canCreate && (
                                                <button
                                                    className="btn btn-danger add-btn me-1"
                                                    onClick={() => setShowCreateTypeModal(true)}
                                                >
                                                    <i className="ri-add-line align-bottom me-1" />
                                                    Create new
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Row className="mb-3 align-items-center">

                                {/* Doc Type */}
                                <Col md={6}>
                                    <div className="d-flex flex-wrap gap-3">
                                        {["FCD", "LIT", "ECN", "SOP", "WI", "ALL"].map(type => (
                                            <div key={type} className="form-check form-check-inline">
                                                <Input
                                                    type="radio"
                                                    name="docType"
                                                    checked={docType === type}
                                                    onChange={() => {
                                                        setDocType(type);

                                                    }}
                                                />
                                                <Label className="form-check-label">
                                                    {type}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </Col>

                                {/* Show Filter */}
                                <Col md={6}>
                                    <div className="d-flex flex-wrap gap-3 justify-content-md-end">
                                        {/* Round R14 (2026) — "AwaitingRelease" chip removed.
                                            Not role-gated here (any user could see/click),
                                            and the dedicated /dms/awaiting-release page
                                            (RoleProtected via dms.release) is the proper
                                            access path. */}
                                        {/* Round DMS-UAT-22: Pending / Denied / Approved quick-filters restore the legacy DMSAwaiting radios (WIP / Denied / Approved). */}
                                        {/* Round DMS-Legacy-Parity-1 (owner): "Saved / Drafts" restores the legacy
                                            "Saved" view — every saved / draft document, shared work anyone can pick up. */}
                                        {/* Round DMS-Legacy-Parity-1b: "Abandoned" lists the legacy leftover copies (status 53)
                                            so Document Control can review and void them, as Ivette does in the legacy window. */}
                                        {["All", "Drafts", "Pending", "Approved", "Released", "Denied", "Archived", "Abandoned", "MyDocs", "AwaitingMyApproval"].map(s => (
                                            <div key={s} className="form-check form-check-inline">
                                                <Input
                                                    type="radio"
                                                    name="show"
                                                    checked={show === s}
                                                    onChange={() => {
                                                        setShow(s);

                                                    }}
                                                />
                                                <Label className="form-check-label">
                                                    {({ AwaitingMyApproval: "Awaiting My Approval", Pending: "Pending Approval",
                                                        Approved: "Approved (Awaiting Release)", Denied: "Needs Revision",
                                                        Archived: "Archived / Obsolete", MyDocs: "My Docs",
                                                        Drafts: "Saved" })[s] || s}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </Col>

                            </Row>
                            <SearchTableDMS
                                data={documents}
                                show={show}
                                loading={loading}
                                onRowDoubleClick={(row) => navigate(`/dms/view/${row.original.dmsid}`)}
                            />
                            {/* {renderPagination()} */}
                        </CardBody>
                    </Card>
                </Col>
                <Modal
                    isOpen={showCreateTypeModal}
                    toggle={() => setShowCreateTypeModal(false)}
                    centered
                    size="lg"
                >
                    <ModalHeader toggle={() => setShowCreateTypeModal(false)}>
                        Create New Document
                    </ModalHeader>

                    <ModalBody>
                        <Row className="g-4">

                            {documentTypes.map((doc) => (
                                <Col md={4} key={doc.type}>
                                    <Card
                                        className="border shadow-sm h-100 cursor-pointer hover-shadow"
                                        onClick={() => handleCreateType(doc.type)}
                                        style={{ cursor: "pointer", transition: "0.2s" }}
                                    >
                                        <CardBody className="text-center">
                                            <div className="avatar-md mx-auto mb-3">
                                                <span className={`avatar-title rounded-circle bg-${doc.color}`}>
                                                    <i className={`${doc.icon} fs-4`} />
                                                </span>
                                            </div>
                                            <h5>{doc.type}</h5>
                                            <p className="text-muted mb-0">{doc.description}</p>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))}

                        </Row>
                    </ModalBody>
                </Modal>

            </div>
        </React.Fragment>
    );
};


export default AllDms;
